import { Box, Paper, Typography, Stack, Alert, List, ListItem, ListItemText } from "@mui/material";

const REQUIRED_VARS = [
  "VITE_AZURE_AD_TENANT_ID",
  "VITE_AZURE_AD_CLIENT_ID",
  "VITE_AZURE_AD_API_SCOPE",
  "VITE_API_BASE_URL"
];

/**
 * Rendered instead of the app when Azure AD configuration is missing.
 * Deliberately never touches the MSAL API - see authConfig.ts for why
 * calling loginRedirect() with a missing clientId is the actual root cause
 * of the "interaction_in_progress" lock this screen exists to prevent.
 */
export default function ConfigMissingScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2
      }}
    >
      <Paper elevation={0} sx={{ p: 4, maxWidth: 520, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Azure AD configuration is missing</Typography>
          <Typography variant="body2" color="text.secondary">
            This app can't sign in until it knows which Entra ID (Azure AD) tenant and app
            registration to use. Create <code>frontend/.env.local</code> (copy it from{" "}
            <code>frontend/.env.example</code>) and fill in real values, then restart{" "}
            <code>npm run dev</code>.
          </Typography>

          <Alert severity="warning">
            Missing or still set to a placeholder value:
          </Alert>

          <List dense disablePadding>
            {REQUIRED_VARS.map((name) => {
              const value = import.meta.env[name as keyof ImportMetaEnv];
              const isMissing = !value || value.startsWith("<");
              return (
                <ListItem key={name} disableGutters>
                  <ListItemText
                    primary={name}
                    secondary={
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: isMissing ? "error.main" : "success.main" }}
                      >
                        {isMissing ? "not set" : "OK"}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          <Typography variant="caption" color="text.secondary">
            See the project README for how to register the Azure AD app and get these values.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
